import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Grid,
  Button,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import AccountServices from 'services/AccountServices';
import SalespersonsService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import BillDetailsDialog from 'components/popups/BillDetailsDialog';
import CustomerService from 'services/CustomerService';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { SUBSCRIPTION_TYPE, SelectFilterStyle, SALE_SOURCES, SALE_STATUS, PRINT_TYPES, INVOICE_LAYOUTS, USER_PERMISSIONS } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DownloadInvoice from 'components/utils/InvoiceDownload/DownloadInvoice';
import SaleInvoice from 'components/utils/InvoiceDownload/SaleInvoice';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';

function ProfitBillSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');

  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { user } = useContext(UserContext);

  const showWriteActions = user?.shop_permissions?.reports_permission !== USER_PERMISSIONS.READ;

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [sorting, setSorting] = useState(null);
  const [filterStatus, setFilterStatus] = useState(SALE_STATUS.active);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    sorting,
    filterStatus,
  });
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);
  const [invoiceToVoid, setInvoiceToVoid] = useState(null);
  const [filterCustomerGroup, setFilterCustomerGroup] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [filterSource, setFilterSource] = useState([]);
  const [filterCustomer, setFilterCustomer] = useState([]);
  const [customerSearchText, setCustomerSearchText] = useState('');

  const CSV_COLUMNS = {
    bill_no: {
      title: tCommon('Bill No'),
      getValue: (datum) => datum.bill_no || datum.fid,
    },
    date: tCommon('Date'),
    time: {
      title: tCommon('Time'),
      getValue: (datum) => DateTimeHelpers.formatServerTimeSegment(datum.time),
    },
    customer_name: tCommon('Customer Name'),
    items: tCommon('No Of Products'),
    tax_amount: {
      title: tCommon('Tax Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.tax_amount || 0);
      },
    },
    cess_amount: {
      title: tCommon('Additional Charges'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.cess_amount || 0);
      },
    },
    purchase_cost: {
      title: tCommon('Purchase Cost'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.purchase_cost || 0);
      },
    },
    purchase_tax: {
      title: tCommon('Purchase Tax'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.purchase_tax || 0);
      },
    },
    purchase_expense: {
      title: tCommon('Expense'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.purchase_expense || 0);
      },
    },
    profit: {
      title: tCommon('Profit'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.profit || 0);
      },
    },
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const arraysAreEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((val) => b.includes(val));
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      !DateTimeHelpers.isSameDate(filterFromDate, prevFilterData.filterFromDate) ||
      !DateTimeHelpers.isSameDate(filterToDate, prevFilterData.filterToDate) ||
      sorting !== prevFilterData.sorting ||
      !arraysAreEqual(filterTerminal, prevFilterData.filterTerminal) ||
      !arraysAreEqual(filterSalesperson, prevFilterData.filterSalesperson) ||
      !arraysAreEqual(filterCustomer, prevFilterData.filterCustomer) ||
      !arraysAreEqual(filterCustomerGroup, prevFilterData.filterCustomerGroup) ||
      !arraysAreEqual(filterSource, prevFilterData.filterSource) ||
      filterStatus !== prevFilterData.filterStatus
    );
  }, [
    filterFromDate,
    filterToDate,
    sorting,
    filterTerminal,
    filterSalesperson,
    filterCustomer,
    filterCustomerGroup,
    filterSource,
    filterStatus,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.sorting,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
    prevFilterData.filterCustomer,
    prevFilterData.filterCustomerGroup,
    prevFilterData.filterSource,
    prevFilterData.filterStatus,
  ]);

  const getCustomerGroups = async () => {
    try {
      const res = await CustomerService.getCustomerGroups();
      setCustomerGroups(res);
    } catch (err) {
      console.error('Failed to fetch customer groups', err);
    }
  };

  useEffect(() => {
    // getMachineName();
    if (subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM) return;
    generateSummeryReport();
    getCustomers();
    getSalespersons();
    getTerminals();
    getCustomerGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Sales Summary Report With Profit'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredItems,
      {
        [tCommon('Total Profit')]: `${shop?.currency || ''} ${summary.totalProfit}`,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${summary.totalAmount}`,
      }
    );
  };

  const generateSummeryReport = () => {
    const { startDate, endDate } = dynamicDateRange('PROFIT_BILL_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadBillSummaryReports({
      startTime: startDate,
      endTime: endDate,
      sort: sorting,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
      customer_ids: filterCustomer,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      sorting,
      filterSalesperson,
      filterTerminal,
      filterCustomerGroup,
      filterSource,
      filterCustomer,
      filterStatus,
    });
  };

  const loadBillSummaryReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    sort = sorting,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
    customer_group_ids = filterCustomerGroup,
    sources = filterSource,
    status = filterStatus,
    customer_ids = filterCustomer,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }

    setLoadingIndicator(true);
    try {
      const timezoneFreeStartTime = DateTimeHelpers.getTimezoneOffsetDate(startTime);
      const timezoneFreeEndTime = DateTimeHelpers.getTimezoneOffsetDate(endTime);

      const data = {
        filter_start_time: timezoneFreeStartTime,
        filter_end_time: timezoneFreeEndTime,
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
        sort_bill_num: sort,
        customer_group_ids: customer_group_ids.length ? customer_group_ids : null,
        sources: sources.length ? sources : null,
        customer_ids: customer_ids.length ? customer_ids : null,
        status,
      };

      const resp = await BillSummaryReportService.getBillSummaryReportWithProfif(data);
      setItems(resp);
      setFilteredItems(resp);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  const getCustomers = async () => {
    const res = await CustomerService.getCustomers();
    setCustomers(res);
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Sales Summary Report With Profit for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredItems, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getBillReportSummary(filteredItems);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'bill_no':
          return tCommon('Total');
        case 'tax_amount':
          return summary.totalTax;
        case 'cess_amount':
          return summary.totalAdditionalTax;
        case 'purchase_cost':
          return summary.totalPurchaseCost;
        case 'purchase_tax':
          return summary.totalPurchaseTax;
        case 'purchase_expense':
          return summary.totalExpence;
        case 'profit':
          return summary.totalProfit;
        case 'total':
          return summary.totalAmount;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PROFIT-SALES-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const handleVoidInvoice = async () => {
    try {
      await BillSummaryReportService.voidInvoice(invoiceToVoid?.id);
      setInvoiceToVoid(null);

      try {
        await AccountServices.syncAccountLedger();
      } catch (e) {
        console.error('Ledger sync failed', e);
      }

      postAlertMessage({ text: 'Void invoice successfully', type: 'success' });
      loadBillSummaryReports();
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  };

  const _getBillReportSummary = (items) => {
    let totalPurchaseCost = 0;
    let totalPurchaseTax = 0;
    let totalExpence = 0;
    let totalAmount = 0;
    let totalProfit = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    let discountAppliedOnItems = 0;
    items.forEach((item) => {
      totalPurchaseCost += Number(item.purchase_cost || 0);
      totalPurchaseTax += Number(item.purchase_tax || 0);
      totalExpence += Number(item.purchase_expense || 0);
      totalAmount += Number(item.total || 0);
      totalProfit += Number(item.profit || 0);
      totalTax += Number(item.tax_amount || 0);
      totalAdditionalTax += Number(item.cess_amount || 0);
      discountAppliedOnItems += Number(item.discount_applied_on_items || 0);
    });

    const profitMargin = totalAmount > 0 ? ((totalProfit / totalAmount) * 100).toFixed(2) : '0.00';

    return {
      totalPurchaseCost: ShopsHelper.getAmountFormatted(shop, totalPurchaseCost),
      totalPurchaseTax: ShopsHelper.getAmountFormatted(shop, totalPurchaseTax),
      totalExpence: ShopsHelper.getAmountFormatted(shop, totalExpence),
      totalProfit: ShopsHelper.getAmountFormatted(shop, totalProfit),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      discountAppliedOnItems: ShopsHelper.getAmountFormatted(shop, discountAppliedOnItems),
      rawProfit: totalProfit,
      rawTotalAmount: totalAmount,
      rawPurchaseCost: totalPurchaseCost,
      rawExpense: totalExpence,
      totalBillsCount: items.length,
      profitMargin,
    };
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredItems(
      items.filter(
        (item) => item.bill_no.toString().includes(value.toLowerCase()) || item.date.includes(value.toLowerCase())
      )
    );
  };

  const handleClickVoidInvoiceBtn = (invoice) => {
    setInvoiceToVoid(invoice);
  };

  const handleCloseSaleDetailsDialog = () => {
    setSelectedBill(null);
  };

  const subscriptionType = shop?.subscription?.type || '';
  const headerData = [
    {
      label: tCommon('Bill No'),
      id: 'bill_no',
      type: 'callback',
      viewRender: (obj) => (obj.bill_no ? obj.bill_no : obj.fid),
    },
    // {
    //   label: 'Counter',
    //   id: 'machine_name',
    //   type: 'callback',
    //   viewRender: (obj) =>
    //     obj.machine_name || obj.machine_no === 998 ? 'POS' : obj.machine_no,
    // },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.formatServerTimeSegment(item.time),
    },
    {
      label: tCommon('Customer'),
      id: 'customer_name',
      type: 'text',
    },
    {
      label: tCommon('No Of Products'),
      id: 'items',
      type: 'text',
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Purchase Cost'),
      id: 'purchase_cost',
      type: 'callback',
      viewRender: (item) => {
        if (item.purchase_cost === '' || isNaN(item.purchase_cost)) {
          return item.purchase_cost;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.purchase_cost)}`;
      },
    },
    {
      label: tCommon('Purchase Tax'),
      id: 'purchase_tax',
      type: 'callback',
      viewRender: (item) => {
        if (item.purchase_tax === '' || isNaN(item.purchase_tax)) {
          return item.purchase_tax;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.purchase_tax)}`;
      },
    },
    {
      label: tCommon('Expense'),
      id: 'purchase_expense',
      type: 'callback',
      viewRender: (item) => {
        if (item.purchase_expense === '' || isNaN(item.purchase_expense)) {
          return item.purchase_expense;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.purchase_expense)}`;
      },
    },
    {
      label: tCommon('Discount'),
      id: 'discount_applied_on_items',
      type: 'floatAmount',
    },
    {
      label: `${tCommon('Profit')}*`,
      id: 'profit',
      type: 'callback',
      viewRender: (item) => {
        if (item.profit === '' || isNaN(item.profit)) {
          return item.profit;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.profit)}`;
      },
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total)}`;
      },
    },
    {
      label: tCommon('Actions'),
      id: 'download',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (bill) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title="View Items" onClick={() => setSelectedBill(bill)}>
              <RemoveRedEyeIcon className={styles.iconBtn} />
            </Tooltip>
            {showWriteActions && (
              <Tooltip title="Void Invoice" onClick={() => handleClickVoidInvoiceBtn(bill)}>
                <RestartAltIcon className={styles.iconBtn} />
              </Tooltip>
            )}
            {shop?.web_invoice_format === INVOICE_LAYOUTS.A4_BACKEND ||
                      shop?.web_invoice_format === INVOICE_LAYOUTS.A5_BACKEND ||
                      !shop?.web_invoice_format ? (
                        <DownloadInvoice bill={bill} showOptions={showWriteActions} />
                      ) : (
                        <SaleInvoice bill={bill} showOptions={showWriteActions} />
                      )}
          </Grid>
        );
      },
    },
  ];

  const summary = _getBillReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>

        {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.PREMIUM && (
          <>
            <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Print Report')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={handlePrintReport}
              >
                <PrintIcon className={styles.actionBtnIcon} />
                {tCommon('Print')}
              </Button>
            </div>
            <div style={{ justifyContent: 'flex-end' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Export As')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={toCsvBtnPressed}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('CSV')}
              </Button>
            </div>
          </>
        )}
      </div>
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM && (
        <RestrictionInfo
          title={tCommon('Feature not available')}
          content={tReport('To get sales summary report with profit option upgrade subscription to premium')}
        />
      )}
      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.PREMIUM && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tReport('Sales Summary Report With Profit for {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                    to: DateTimeHelpers.convertDateToDMY(filterToDate),
                  })}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      handleSearch(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tReport('Search by bill no, date')}
                  />
                  <Tooltip title="Search" placement="bottom">
                    <SearchIcon className={styles.searchIcon} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange
                  setFilterFromDate={setFilterFromDate}
                  setFilterToDate={setFilterToDate}
                  storageKey="PROFIT_BILL_SUMMARY"
                />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Terminal')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterTerminal}
                      onChange={(e) => setFilterTerminal(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterTerminal) => {
                        if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterTerminal.map((id) => terminals.find((tr) => tr.id === id).name);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {terminals.map((terminal) => (
                        <MenuItem key={terminal.id} value={terminal.id}>
                          <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                          <ListItemText primary={terminal.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Salesperson')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterSalesperson}
                      onChange={(e) => setFilterSalesperson(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterSalesperson) => {
                        if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterSalesperson.map(
                          (id) => salesPersons.find((person) => person.id === id).name
                        );
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {salesPersons.map((salesperson) => (
                        <MenuItem key={salesperson.id} value={salesperson.id}>
                          <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                          <ListItemText primary={salesperson?.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Customer')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="customer-checkbox-label"
                      id="customer-checkbox"
                      multiple
                      displayEmpty
                      value={filterCustomer}
                      onChange={(e) => setFilterCustomer(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterCustomer) => {
                        if (filterCustomer.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterCustomer.map(
                          (id) => (customers || []).find((person) => person.id === id)?.name || ''
                        );
                        return selectedNames.filter((n) => n).join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      <ListSubheader>
                        <TextField
                          size="small"
                          autoFocus
                          placeholder={tReport('Search by bill no, date')}
                          fullWidth
                          InputProps={{
                            startAdornment: <SearchIcon style={{ color: 'grey' }} />,
                          }}
                          onChange={(e) => setCustomerSearchText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key !== 'Escape') {
                              e.stopPropagation();
                            }
                          }}
                        />
                      </ListSubheader>
                      {customers &&
                        customers.length > 0 &&
                        customers
                          .filter((customer) => customer.name.toLowerCase().includes(customerSearchText.toLowerCase()))
                          .map((customer) => (
                            <MenuItem key={customer.id} value={customer.id}>
                              <Checkbox checked={filterCustomer.includes(customer.id)} color="primary" />
                              <ListItemText primary={customer?.name} />
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Bill no order')}</label>
                </div>
                <div>
                  <TextField
                    select
                    name="billNo"
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{ width: 135 }}
                    value={sorting}
                    onChange={(event) => {
                      setSorting(event.target.value);
                      // loadBillSummaryReports({ sort: event.target.value });
                    }}
                    displayEmpty
                  >
                    <MenuItem value={null}>
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={'DESC'}>{tCommon('Descending')}</MenuItem>
                    <MenuItem value={'ASC'}>{tCommon('Ascending')}</MenuItem>
                  </TextField>
                </div>
              </div>

              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Customer Group')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      multiple
                      displayEmpty
                      value={filterCustomerGroup}
                      onChange={(e) => setFilterCustomerGroup(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(selected) => {
                        if (selected.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = selected
                          .map((id) => customerGroups.find((group) => group.id === id)?.name)
                          .filter(Boolean);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {customerGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          <Checkbox checked={filterCustomerGroup.includes(group.id)} color="primary" />
                          <ListItemText primary={group.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Sale source')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      multiple
                      displayEmpty
                      value={filterSource}
                      onChange={(e) => setFilterSource(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(selected) => {
                        if (selected.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = selected
                          .map((id) => SALE_SOURCES.find((source) => source.id === id)?.label)
                          .filter(Boolean);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {SALE_SOURCES.map((source) => (
                        <MenuItem key={source.id} value={source.id}>
                          <Checkbox checked={filterSource.includes(source.id)} color="primary" />
                          <ListItemText primary={source.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Sale Status')}</label>
                </div>
                <div>
                  <TextField
                    select
                    name="status"
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{ width: 135 }}
                    value={filterStatus}
                    onChange={(event) => {
                      setFilterStatus(event.target.value);
                    }}
                  >
                    {Object.values(SALE_STATUS).map((s) => {
                      return (
                        <MenuItem key={s} value={s}>
                          {tCommon(s)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Generate Report')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generateSummeryReport}
                    disabled={isShowGenerateButton === false || loadingIndicator}
                  >
                    {loadingIndicator ? (
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    ) : (
                      <SyncIcon className={styles.actionBtnIcon} />
                    )}
                    {tCommon('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {isShowGenerateButton === null ? (
            <Info
              title={tReport('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : (
            items &&
            (filteredItems && filteredItems.length ? (
              <>
                <div className={styles.summaryContainer}>
                  <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                    <span className={styles.cardTitle}>{tCommon('Total Amount')}</span>
                    <div className={styles.cardValue}>
                      {`${shop?.currency || ''} ${summary.totalAmount}`}
                    </div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Total Bills')}: <strong className={styles.detailsValue}>{summary.totalBillsCount}</strong>
                      </span>
                    </div>
                  </div>

                  <div className={`${styles.summaryCard} ${summary.rawProfit >= 0 ? styles.cardGreen : styles.cardRed}`}>
                    <span className={styles.cardTitle}>{tCommon('Profit')}</span>
                    <div
                      className={styles.cardValue}
                      style={{ color: summary.rawProfit >= 0 ? '#2e7d32' : '#c62828' }}
                    >
                      {`${shop?.currency || ''} ${summary.totalProfit}`}
                    </div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Profit Margin (%)')}: <strong className={styles.detailsValue}>{summary.profitMargin}%</strong>
                      </span>
                    </div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
                    <span className={styles.cardTitle}>{tCommon('Purchase Cost')}</span>
                    <div className={styles.cardValue}>
                      {`${shop?.currency || ''} ${summary.totalPurchaseCost}`}
                    </div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Purchase Tax')}: <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${summary.totalPurchaseTax}`}</strong>
                      </span>
                    </div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardPurple}`}>
                    <span className={styles.cardTitle}>{tCommon('Expense')}</span>
                    <div className={styles.cardValue}>
                      {`${shop?.currency || ''} ${summary.totalExpence}`}
                    </div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Tax Amount')}: <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${summary.totalTax}`}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.tableContainer}>
                  <Grayout open={isShowGenerateButton} />
                  <DataTable
                    columns={headerData}
                    rows={filteredItems ? filteredItems : items}
                    rowKey="id"
                    summary={{
                      name: tCommon('Total'),
                      purchase_cost: `${shop?.currency} ${summary.totalPurchaseCost}`,
                      purchase_tax: `${shop?.currency} ${summary.totalPurchaseTax}`,
                      purchase_expense: `${shop?.currency} ${summary.totalExpence}`,
                      tax_amount: `${shop?.currency || ''} ${summary.totalTax}`,
                      cess_amount: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
                      discount_applied_on_items: `${shop?.currency || ''}  ${summary.discountAppliedOnItems}`,
                      profit: `${shop?.currency} ${summary.totalProfit}`,
                      total: `${shop?.currency} ${summary.totalAmount}`,
                    }}
                    pagination={true}
                  />
                </div>
              </>
            ) : (
              !loadingIndicator && (
                <Info
                  severity="warning"
                  title={tReport('Oh no, there are no sales matching to your filter')}
                  content={tReport(
                    'There are no sales matching to your filter. Please verify your inputs to generate report.'
                  )}
                />
              )
            ))
          )}
        </>
      )}

      {selectedBill && (
        <BillDetailsDialog
          customers={customers}
          handleClose={handleCloseSaleDetailsDialog}
          selectedSale={selectedBill}
          shouldShowProfit={true}
        />
      )}
      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      <ConfirmPopup
        data={invoiceToVoid}
        handleClose={() => setInvoiceToVoid(null)}
        handleOkay={handleVoidInvoice}
        alertTitle={tCommon('Confirm void invoice')}
        successBtnName={tCommon('Confirm')}
        alertContent={tCommon(
          'This action will permanently void the sale and cannot be undone. Are you sure you want to proceed?'
        )}
      />
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(ProfitBillSummaryReport);
